import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { ArrowLeft, Car, User, Phone, Mail, Lock, Loader2, CheckCircle2, AlertCircle, IdCard, Palette, Tag, Truck, Search, Copy, Send } from 'lucide-react';
interface DriverVehicleForm {
  full_name: string;
  national_id: string;
  phone: string;
  email: string;
  temporary_password: string;
  is_active: boolean;
  plate: string;
  vehicle_type: string;
  brand: string;
  model: string;
  color: string;
}
interface CreateResult {
  driver_id?: number;
  full_name?: string;
  national_id?: string;
  phone?: string;
  email?: string;
  plate?: string;
  vehicle_type?: string;
  brand?: string;
  model?: string;
  color?: string;
  vehicle_reused?: boolean;
}
const VEHICLE_TYPES = ['Carro', 'Moto', 'Bicicleta', 'Camioneta'];
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20';
export function CreateDriverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionChecked, setSessionChecked] = useState(false);
  // ─── Session guard (belt-and-suspenders) ───
  useEffect(() => {
    async function guard() {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      console.log('[CreateDriverPage] Session guard:', {
        pathname: location.pathname,
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        userId: session?.user?.id ?? null
      });
      if (!session?.access_token) {
        console.log('[CreateDriverPage] No session — redirecting to /admin/login');
        navigate('/admin/login', {
          state: {
            from: location.pathname
          },
          replace: true
        });
        return;
      }
      setSessionChecked(true);
    }
    guard();
  }, [navigate, location.pathname]);
  const [form, setForm] = useState<DriverVehicleForm>({
    full_name: '',
    national_id: '',
    phone: '',
    email: '',
    temporary_password: '',
    is_active: true,
    plate: '',
    vehicle_type: 'Carro',
    brand: '',
    model: '',
    color: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateResult | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  // Cédula lookup state
  const [cedulaChecking, setCedulaChecking] = useState(false);
  const [existingDriver, setExistingDriver] = useState<{
    full_name: string;
    phone: string;
  } | null>(null);
  const [cedulaChecked, setCedulaChecked] = useState(false);
  const cedulaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Debounced cédula lookup
  useEffect(() => {
    const val = form.national_id.trim();
    // Reset state when input changes
    setExistingDriver(null);
    setCedulaChecked(false);
    if (!val || val.length < 5) return;
    if (cedulaTimerRef.current) clearTimeout(cedulaTimerRef.current);
    cedulaTimerRef.current = setTimeout(async () => {
      setCedulaChecking(true);
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('drivers').select('full_name, phone').eq('national_id', val).maybeSingle();
        if (!queryError && data) {
          setExistingDriver({
            full_name: data.full_name,
            phone: data.phone
          });
        } else {
          setExistingDriver(null);
        }
        setCedulaChecked(true);
      } catch (err) {
        console.error('Cédula lookup error:', err);
      } finally {
        setCedulaChecking(false);
      }
    }, 400);
    return () => {
      if (cedulaTimerRef.current) clearTimeout(cedulaTimerRef.current);
    };
  }, [form.national_id]);
  const updateField = (field: keyof DriverVehicleForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.full_name.trim()) return setError('El nombre es obligatorio.');
    if (!form.national_id.trim()) return setError('La cédula es obligatoria.');
    if (!form.phone.trim()) return setError('El teléfono es obligatorio.');
    if (!form.email.trim()) return setError('El correo es obligatorio.');
    if (!form.temporary_password || form.temporary_password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres.');
    if (!form.plate.trim()) return setError('La placa del vehículo es obligatoria.');
    if (!form.vehicle_type.trim()) return setError('El tipo de vehículo es obligatorio.');
    setIsSubmitting(true);
    // Capture credentials BEFORE any async calls
    const capturedCredentials = {
      name: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.temporary_password
    };
    const payload = {
      full_name: form.full_name.trim(),
      national_id: form.national_id.trim(),
      phone: form.phone.trim(),
      email: form.email.trim().toLowerCase(),
      temporary_password: form.temporary_password,
      is_active: form.is_active,
      vehicle: {
        plate: form.plate.trim().toUpperCase(),
        vehicle_type: form.vehicle_type,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        color: form.color.trim() || null
      }
    };
    console.log('[CreateDriver] payload:', payload);
    // ─── Step 1: Force session refresh to get a fresh token ───
    console.log('[CreateDriver] Step 1: Refreshing session...');
    const {
      data: refreshData,
      error: refreshError
    } = await supabase.auth.refreshSession();
    if (refreshError) {
      console.warn('[CreateDriver] Session refresh failed:', refreshError.message, '— falling back to getSession');
    } else {
      console.log('[CreateDriver] Session refreshed successfully');
    }
    // ─── Step 2: Get the (now-fresh) session ───
    const {
      data: sessionData,
      error: sessionError
    } = await supabase.auth.getSession();
    const session = sessionData?.session;
    const accessToken = session?.access_token;
    console.log('[CreateDriver] Session state:', {
      hasSession: !!session,
      accessToken: accessToken ? 'present (' + accessToken.substring(0, 20) + '...)' : 'MISSING',
      tokenLength: accessToken?.length ?? 0,
      userId: session?.user?.id ?? null,
      email: session?.user?.email ?? null,
      expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
      isExpired: session?.expires_at ? Date.now() / 1000 > session.expires_at : 'no session',
      secondsUntilExpiry: session?.expires_at ? Math.round(session.expires_at - Date.now() / 1000) : null,
      sessionError: sessionError?.message ?? null,
      refreshError: refreshError?.message ?? null
    });
    if (!accessToken) {
      console.error('[CreateDriver] NO ACCESS TOKEN — cannot call edge function');
      setError('No hay sesión activa. Inicia sesión primero en /admin/login');
      setIsSubmitting(false);
      return;
    }
    // ─── Step 3: Verify token is valid with getUser() before sending ───
    console.log('[CreateDriver] Step 3: Verifying token with getUser()...');
    const {
      data: userData,
      error: userError
    } = await supabase.auth.getUser();
    console.log('[CreateDriver] getUser() result:', {
      success: !!userData?.user && !userError,
      userId: userData?.user?.id ?? null,
      email: userData?.user?.email ?? null,
      error: userError?.message ?? null
    });
    if (userError || !userData?.user) {
      console.error('[CreateDriver] Token is INVALID — getUser() failed:', userError?.message);
      setError('Tu sesión ha expirado. Por favor inicia sesión de nuevo.');
      setIsSubmitting(false);
      // Force re-login
      navigate('/admin/login', {
        state: {
          from: location.pathname
        },
        replace: true
      });
      return;
    }
    // ─── Step 4: Build and send request ───
    const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/create-driver-with-vehicle`;
    const requestHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY
    };
    console.log('[CreateDriver] Request details:', {
      url: edgeFunctionUrl,
      method: 'POST',
      authHeaderPrefix: `Bearer ${accessToken.substring(0, 20)}...`,
      authHeaderLength: `Bearer ${accessToken}`.length,
      apiKeyPrefix: SUPABASE_ANON_KEY.substring(0, 20) + '...',
      tokenIsJWT: accessToken.startsWith('eyJ')
    });
    let data: any = null;
    try {
      const res = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(payload)
      });
      data = await res.json();
      console.log('[CreateDriver] Response:', {
        status: res.status,
        statusText: res.statusText,
        data
      });
      if (!res.ok) {
        console.error('backend error:', data);
        const raw = (data?.error || data?.details || data?.message || '').toLowerCase();
        let friendlyMsg = data?.error || data?.details || data?.message || `Error del servidor (${res.status})`;
        if (raw.includes('national_id') || raw.includes('cédula') || raw.includes('duplicate') && raw.includes('cedula')) {
          friendlyMsg = 'Ya existe un conductor registrado con esta cédula.';
        } else if (raw.includes('phone') || raw.includes('teléfono') || raw.includes('telefono')) {
          friendlyMsg = 'Ya existe un conductor registrado con este teléfono.';
        } else if (raw.includes('email') || raw.includes('correo') || raw.includes('already registered') || raw.includes('already been registered')) {
          friendlyMsg = 'Ya existe un usuario con este correo.';
        }
        setError(friendlyMsg);
        setIsSubmitting(false);
        return;
      }
    } catch (fetchErr: any) {
      console.error('fetch error:', fetchErr);
      setError(fetchErr.message || 'Error de conexión con el servidor.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    if (!data?.success) {
      setError(data?.error || data?.details || 'Error al crear el conductor.');
      return;
    }
    setCreatedCredentials(capturedCredentials);
    setResult({
      driver_id: data.driver?.id,
      full_name: data.driver?.full_name || capturedCredentials.name,
      national_id: data.driver?.national_id || form.national_id,
      phone: data.driver?.phone || form.phone,
      email: data.auth_user?.email || capturedCredentials.email,
      plate: data.vehicle?.plate || form.plate,
      vehicle_type: data.vehicle?.vehicle_type || form.vehicle_type,
      brand: data.vehicle?.brand || form.brand,
      model: data.vehicle?.model || form.model,
      color: data.vehicle?.color || form.color,
      vehicle_reused: data.vehicle_reused === true
    });
  };
  const [copied, setCopied] = useState(false);
  const getCredentialsText = () => {
    if (!createdCredentials) return '';
    return `Hola ${createdCredentials.name},\n\nTu cuenta de conductor en NOIZA ha sido creada.\n\nEmail: ${createdCredentials.email}\nContraseña temporal: ${createdCredentials.password}\n\nPor favor cambia tu contraseña después de tu primer inicio de sesión.\n\nEquipo NOIZA`;
  };
  const handleCopyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(getCredentialsText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = getCredentialsText();
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  const handleSendByEmail = () => {
    if (!createdCredentials) return;
    const subject = encodeURIComponent('Acceso a tu cuenta de conductor en NOIZA');
    const body = encodeURIComponent(`Hola ${createdCredentials.name},\n\n` + `Tu cuenta de conductor en NOIZA ha sido creada exitosamente.\n\n` + `Datos de acceso:\n` + `Email: ${createdCredentials.email}\n` + `Contraseña temporal: ${createdCredentials.password}\n\n` + `Importante: Por favor cambia tu contraseña después de tu primer inicio de sesión.\n\n` + `¡Bienvenido al equipo!\n` + `Equipo NOIZA`);
    window.open(`mailto:${createdCredentials.email}?subject=${subject}&body=${body}`, '_blank');
  };
  if (!sessionChecked) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3236">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-3237" />
      </div>;
  }
  if (result) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-3238">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-3239">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-3240">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-3241" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-3242">¡Conductor creado!</h1>
          <p className="text-white/50" data-id="element-3243">
            Cuenta, vehículo y asignación creados exitosamente.
          </p>

          {/* Credentials Card */}
          <div className="bg-[#141414] border border-[#006847]/30 rounded-2xl p-5 text-left space-y-4" data-id="element-3244">
            <div className="flex items-center justify-between" data-id="element-3245">
              <p className="text-xs font-bold text-[#00a870] uppercase tracking-widest" data-id="element-3246">
                Credenciales de acceso
              </p>
              <Lock className="w-4 h-4 text-[#00a870]/50" data-id="element-3247" />
            </div>
            <div className="space-y-3 text-sm" data-id="element-3248">
              <div className="flex justify-between" data-id="element-3249">
                <span className="text-white/50" data-id="element-3250">Nombre</span>
                <strong data-id="element-3251">{createdCredentials?.name}</strong>
              </div>
              <div className="flex justify-between" data-id="element-3252">
                <span className="text-white/50" data-id="element-3253">Email</span>
                <strong className="font-mono text-xs" data-id="element-3254">
                  {createdCredentials?.email}
                </strong>
              </div>
              <div className="flex justify-between items-center" data-id="element-3255">
                <span className="text-white/50" data-id="element-3256">Contraseña temporal</span>
                <strong className="font-mono text-xs bg-white/5 px-2 py-1 rounded-lg" data-id="element-3257">
                  {createdCredentials?.password}
                </strong>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2" data-id="element-3258">
              <button onClick={handleCopyCredentials} className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-95 ${copied ? 'bg-[#006847]/20 text-[#00a870] border border-[#006847]/30' : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'}`} data-id="element-3259">
                {copied ? <>
                    <CheckCircle2 className="w-4 h-4" data-id="element-3260" /> Copiado
                  </> : <>
                    <Copy className="w-4 h-4" data-id="element-3261" /> Copiar credenciales
                  </>}
              </button>
              <button onClick={handleSendByEmail} className="flex-1 py-2.5 bg-[#006847] hover:bg-[#005a3d] rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-95" data-id="element-3262">
                <Send className="w-4 h-4" data-id="element-3263" /> Enviar por email
              </button>
            </div>
          </div>

          {/* Driver info */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-3264">
            <p className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1" data-id="element-3265">
              Conductor
            </p>
            <div className="flex justify-between" data-id="element-3266">
              <span className="text-white/50" data-id="element-3267">Cédula</span>
              <strong className="font-mono text-xs" data-id="element-3268">
                {result.national_id}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-3269">
              <span className="text-white/50" data-id="element-3270">Teléfono</span>
              <strong data-id="element-3271">{result.phone}</strong>
            </div>
            {result.driver_id && <div className="flex justify-between" data-id="element-3272">
                <span className="text-white/50" data-id="element-3273">Driver ID</span>
                <strong className="font-mono" data-id="element-3274">{result.driver_id}</strong>
              </div>}
          </div>

          {/* Vehicle info */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-3275">
            <div className="flex items-center justify-between mb-1" data-id="element-3276">
              <p className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-3277">
                Vehículo
              </p>
              {result.vehicle_reused ? <span className="text-xs font-bold bg-yellow-500/15 text-yellow-400 px-2.5 py-0.5 rounded-lg" data-id="element-3278">
                  Reutilizado
                </span> : <span className="text-xs font-bold bg-[#006847]/20 text-[#00a870] px-2.5 py-0.5 rounded-lg" data-id="element-3279">
                  Nuevo
                </span>}
            </div>
            <div className="flex justify-between" data-id="element-3280">
              <span className="text-white/50" data-id="element-3281">Placa</span>
              <strong className="font-mono" data-id="element-3282">{result.plate}</strong>
            </div>
            <div className="flex justify-between" data-id="element-3283">
              <span className="text-white/50" data-id="element-3284">Tipo</span>
              <strong data-id="element-3285">{result.vehicle_type}</strong>
            </div>
            {result.brand && <div className="flex justify-between" data-id="element-3286">
                <span className="text-white/50" data-id="element-3287">Marca</span>
                <strong data-id="element-3288">{result.brand}</strong>
              </div>}
            {result.model && <div className="flex justify-between" data-id="element-3289">
                <span className="text-white/50" data-id="element-3290">Modelo</span>
                <strong data-id="element-3291">{result.model}</strong>
              </div>}
            {result.color && <div className="flex justify-between" data-id="element-3292">
                <span className="text-white/50" data-id="element-3293">Color</span>
                <strong data-id="element-3294">{result.color}</strong>
              </div>}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-left" data-id="element-3295">
            <p className="text-sm text-yellow-300" data-id="element-3296">
              ⚠️ La contraseña temporal solo se muestra ahora. Asegúrate de
              compartirla de forma segura. El conductor debe cambiarla en su
              primer inicio de sesión.
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-3297">
            <button onClick={() => {
            setResult(null);
            setCreatedCredentials(null);
            setCopied(false);
            setForm({
              full_name: '',
              national_id: '',
              phone: '',
              email: '',
              temporary_password: '',
              is_active: true,
              plate: '',
              vehicle_type: 'Carro',
              brand: '',
              model: '',
              color: ''
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-3298">
              Crear otro
            </button>
            <button onClick={() => navigate('/admin/drivers')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-3299">
              Ver conductores
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-3300">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-3301">
        <DriverAdminTabs data-id="element-3302" />

        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-3303">
          <button onClick={() => navigate('/admin/drivers')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-3304">
            <ArrowLeft className="w-5 h-5" data-id="element-3305" />
          </button>
          <div data-id="element-3306">
            <h1 className="text-2xl font-bold" data-id="element-3307">Nuevo Conductor</h1>
            <p className="text-white/40 text-sm" data-id="element-3308">
              Registra conductor y vehículo en un solo paso
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3309">
          {/* SECTION 1 — Driver */}
          <div className="space-y-4" data-id="element-3310">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-3311">
              <User className="w-4 h-4 text-[#00a870]" data-id="element-3312" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-3313">
                Datos del conductor
              </h2>
            </div>

            {/* Name */}
            <div data-id="element-3314">
              <label className={labelClass} data-id="element-3315">Nombre completo</label>
              <div className="relative" data-id="element-3316">
                <User className={iconClass} data-id="element-3317" />
                <input type="text" value={form.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Juan Pérez" className={inputClass} required data-id="element-3318" />
              </div>
            </div>

            {/* National ID with live lookup */}
            <div data-id="element-3319">
              <label className={labelClass} data-id="element-3320">Cédula</label>
              <div className="relative" data-id="element-3321">
                <IdCard className={iconClass} data-id="element-3322" />
                <input type="text" value={form.national_id} onChange={e => updateField('national_id', e.target.value)} placeholder="Ej: 304590658" className={`${inputClass} ${existingDriver ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/50' : ''}`} required data-id="element-3323" />
                {cedulaChecking && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" data-id="element-3324" />}
                {!cedulaChecking && cedulaChecked && !existingDriver && <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00a870]" data-id="element-3325" />}
              </div>

              {/* Cédula lookup feedback */}
              {cedulaChecking && <p className="text-xs text-white/30 mt-1.5 pl-1" data-id="element-3326">
                  Verificando cédula...
                </p>}
              {!cedulaChecking && cedulaChecked && existingDriver && <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3327">
                  <p className="text-sm font-bold text-red-400" data-id="element-3328">
                    Ya existe un conductor registrado con esta cédula
                  </p>
                  <div className="mt-2 space-y-1 text-xs text-red-300/70" data-id="element-3329">
                    <p data-id="element-3330">
                      Nombre:{' '}
                      <strong className="text-red-300" data-id="element-3331">
                        {existingDriver.full_name}
                      </strong>
                    </p>
                    <p data-id="element-3332">
                      Teléfono:{' '}
                      <strong className="text-red-300" data-id="element-3333">
                        {existingDriver.phone}
                      </strong>
                    </p>
                  </div>
                </div>}
              {!cedulaChecking && cedulaChecked && !existingDriver && <p className="text-xs text-[#00a870]/70 mt-1.5 pl-1 flex items-center gap-1" data-id="element-3334">
                  <CheckCircle2 className="w-3 h-3" data-id="element-3335" />
                  No existe un conductor con esta cédula
                </p>}
            </div>

            {/* Phone */}
            <div data-id="element-3336">
              <label className={labelClass} data-id="element-3337">Teléfono</label>
              <div className="relative" data-id="element-3338">
                <Phone className={iconClass} data-id="element-3339" />
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="88888888" className={inputClass} required data-id="element-3340" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-3341">
              <label className={labelClass} data-id="element-3342">Correo electrónico</label>
              <div className="relative" data-id="element-3343">
                <Mail className={iconClass} data-id="element-3344" />
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="conductor@noiza.com" className={inputClass} required data-id="element-3345" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-3346">
              <label className={labelClass} data-id="element-3347">Contraseña temporal</label>
              <div className="relative" data-id="element-3348">
                <Lock className={iconClass} data-id="element-3349" />
                <input type="text" value={form.temporary_password} onChange={e => updateField('temporary_password', e.target.value)} placeholder="Mínimo 6 caracteres" className={`${inputClass} font-mono`} required minLength={6} data-id="element-3350" />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-3351">
              <span className="text-sm font-medium text-white/70" data-id="element-3352">
                Conductor activo
              </span>
              <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-3353">
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-3354" />
              </button>
            </div>
          </div>

          {/* SECTION 2 — Vehicle */}
          <div className="space-y-4" data-id="element-3355">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-3356">
              <Car className="w-4 h-4 text-[#00a870]" data-id="element-3357" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-3358">
                Datos del vehículo
              </h2>
            </div>

            {/* Plate */}
            <div data-id="element-3359">
              <label className={labelClass} data-id="element-3360">Placa</label>
              <div className="relative" data-id="element-3361">
                <Tag className={iconClass} data-id="element-3362" />
                <input type="text" value={form.plate} onChange={e => updateField('plate', e.target.value.toUpperCase())} placeholder="Ej: BRC713" className={`${inputClass} font-mono uppercase`} required data-id="element-3363" />
              </div>
            </div>

            {/* Vehicle Type */}
            <div data-id="element-3364">
              <label className={labelClass} data-id="element-3365">Tipo de vehículo</label>
              <div className="relative" data-id="element-3366">
                <Truck className={iconClass} data-id="element-3367" />
                <select value={form.vehicle_type} onChange={e => updateField('vehicle_type', e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required data-id="element-3368">
                  {VEHICLE_TYPES.map(type => <option key={type} value={type} className="bg-[#141414] text-white" data-id="element-3369">
                      {type}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Brand */}
            <div data-id="element-3370">
              <label className={labelClass} data-id="element-3371">
                Marca{' '}
                <span className="text-white/20 normal-case" data-id="element-3372">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3373">
                <Car className={iconClass} data-id="element-3374" />
                <input type="text" value={form.brand} onChange={e => updateField('brand', e.target.value)} placeholder="Ej: Toyota" className={inputClass} data-id="element-3375" />
              </div>
            </div>

            {/* Model */}
            <div data-id="element-3376">
              <label className={labelClass} data-id="element-3377">
                Modelo{' '}
                <span className="text-white/20 normal-case" data-id="element-3378">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3379">
                <Car className={iconClass} data-id="element-3380" />
                <input type="text" value={form.model} onChange={e => updateField('model', e.target.value)} placeholder="Ej: Corolla" className={inputClass} data-id="element-3381" />
              </div>
            </div>

            {/* Color */}
            <div data-id="element-3382">
              <label className={labelClass} data-id="element-3383">
                Color{' '}
                <span className="text-white/20 normal-case" data-id="element-3384">(opcional)</span>
              </label>
              <div className="relative" data-id="element-3385">
                <Palette className={iconClass} data-id="element-3386" />
                <input type="text" value={form.color} onChange={e => updateField('color', e.target.value)} placeholder="Ej: Blanco" className={inputClass} data-id="element-3387" />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3388">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3389" />
              <div className="text-sm whitespace-pre-line" data-id="element-3390">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting || !!existingDriver} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-3391">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-3392" />
                Creando conductor y vehículo...
              </> : existingDriver ? <>
                <AlertCircle className="w-5 h-5" data-id="element-3393" />
                Conductor ya existe
              </> : <>
                <User className="w-5 h-5" data-id="element-3394" />
                Crear Conductor con Vehículo
              </>}
          </button>
        </form>
      </div>
    </div>;
}